import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from './LoginForm';
import { SignUpForm } from './SignUpForm';
import { useFirebase } from '../../lib/firebase/FirebaseProvider';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
  redirectTo?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'login',
  redirectTo
}) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot-password'>(initialMode);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const { user, sendPasswordResetEmail, loading, error, clearError } = useFirebase();
  const navigate = useNavigate();

  // Auto-close modal and redirect when user successfully authenticates
  useEffect(() => {
    if (user && isOpen) {
      onClose();
      if (redirectTo) {
        navigate(redirectTo);
      }
    }
  }, [user, isOpen, onClose, redirectTo, navigate]);

  if (!isOpen) return null;

  const handleToggleMode = () => {
    clearError();
    setMode(mode === 'login' ? 'signup' : 'login');
  };

  const handleForgotPassword = () => {
    clearError();
    setMode('forgot-password');
  };

  const handleBackToLogin = () => {
    clearError();
    setResetSent(false);
    setResetEmail('');
    setMode('login');
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;

    await sendPasswordResetEmail(resetEmail);
    if (!error) {
      setResetSent(true);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={handleOverlayClick}
    >
      <div className="relative max-w-md w-full">
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 bg-teddy rounded-full p-2 shadow-card hover:bg-teddy-500 z-10"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {mode === 'login' && (
          <LoginForm
            onToggleMode={handleToggleMode}
            onForgotPassword={handleForgotPassword}
          />
        )}

        {mode === 'signup' && (
          <SignUpForm onToggleMode={handleToggleMode} />
        )}

        {mode === 'forgot-password' && (
          <div className="bg-teddy shadow-card rounded-lg px-8 pt-6 pb-8 mb-4">
            <h2 className="text-2xl font-bold text-center text-urban mb-6">
              Reset Password
            </h2>

            {resetSent ? (
              <div className="text-center">
                <div className="bg-verge-100 border border-verge-400 text-verge-700 px-4 py-3 rounded mb-4">
                  Password reset email sent! Check your inbox and follow the instructions to reset your password.
                </div>
                <button
                  onClick={handleBackToLogin}
                  className="text-cardboard hover:text-cardboard-700 font-medium"
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-rust-100 border border-rust-400 text-rust-700 px-4 py-3 rounded mb-4">
                    {error}
                  </div>
                )}

                <p className="text-urban-300 text-center mb-6">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <label htmlFor="resetEmail" className="block text-urban text-sm font-bold mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="resetEmail"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="shadow-card appearance-none border border-urban-300 rounded w-full py-2 px-3 bg-sky-neutral text-urban leading-tight focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !resetEmail.trim()}
                    className="w-full bg-cardboard hover:bg-cardboard-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending...' : 'Send Reset Email'}
                  </button>
                </form>

                <div className="text-center mt-6">
                  <button
                    onClick={handleBackToLogin}
                    className="text-cardboard hover:text-cardboard-700 font-medium"
                  >
                    Back to Sign In
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render the modal using a portal to the document body
  return createPortal(modalContent, document.body);
};